import type { ReactNode } from 'react';
import { Button } from '../Button/Button';
import { SparkIcon } from '../SparkIcon';
import { OutlineFeedbackIcon } from '../shared/OutlineFeedbackIcon';
import type { FeedbackType } from '../shared/feedbackHelpers';
import './AlertBanner.css';
import '../shared/feedbackIcons.css';

export type AlertBannerButtonType = 'default' | 'text';

/** Alert banner with optional action and close buttons. Supports success, warning, error, and info feedback variants. */
export type AlertBannerProps = {
  text?: ReactNode;
  /** Visual style of the banner: success, warning, error, or info. */
  feedback?: FeedbackType;
  /** Render the action as a `'default'` pill button or a `'text'` link-style button. */
  buttonType?: AlertBannerButtonType;
  showIcon?: boolean;
  showButton?: boolean;
  showCloseButton?: boolean;
  buttonLabel?: string;
  /**
   * Custom action node (e.g. a Fix menu). When set, rendered instead of the
   * default `buttonLabel` button.
   */
  action?: ReactNode;
  className?: string;
  /** Fired when the action button is clicked. */
  onActionClick?: () => void;
  /** Fired when the close (dismiss) button is clicked. */
  onCloseClick?: () => void;
};

export function AlertBanner({
  text = '{Project Name} was added to your Projects',
  feedback = 'success',
  buttonType = 'default',
  showIcon = true,
  showButton = true,
  showCloseButton = true,
  buttonLabel = 'Button',
  action,
  className = '',
  onActionClick,
  onCloseClick,
}: AlertBannerProps) {
  const actionNode = action
    ? action
    : showButton
      ? (
          buttonType === 'text' ? (
            <Button variant="link" size="medium" type="button" onClick={onActionClick}>
              {buttonLabel}
            </Button>
          ) : (
            <button type="button" className="spark-alert-banner-action" onClick={onActionClick}>
              {buttonLabel}
            </button>
          )
        )
      : null;

  return (
    <div
      className={['spark-alert-banner', `spark-alert-banner-${feedback}`, className].filter(Boolean).join(' ')}
      role="status"
    >
      <div className="spark-alert-banner-content">
        {showIcon ? <OutlineFeedbackIcon feedback={feedback} className="spark-alert-banner-icon" /> : null}
        <div className="spark-alert-banner-text">{text}</div>
        {actionNode}
      </div>
      {showCloseButton ? (
        <div className="spark-alert-banner-close-wrap">
          <Button
            variant="ghost"
            size="tiny"
            type="button"
            className="spark-alert-banner-close"
            aria-label="Dismiss alert"
            iconSlug="close"
            icon={<SparkIcon slug="close" size="small" />}
            onClick={onCloseClick}
          />
        </div>
      ) : null}
    </div>
  );
}
